import React, { useState, useEffect } from 'react'
import { IoIosArrowBack } from 'react-icons/io'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { LiaCalendarTimes } from 'react-icons/lia'
import { FaRegStar } from 'react-icons/fa'
import { FaBookOpen } from 'react-icons/fa6'
import { GoBook } from 'react-icons/go'
import api from '../../api/api'
import { useProfileStore } from '../../store/useUserStore'


type HomeworkStatus = 'active' | 'rate' | 'close'

interface HomeworkItem {
    id: number
    title: string
    description: string
    course_name: string
    branch_name: string
    created_by_name: string
    start_date: string
    end_date: string
    status: string
    is_active: boolean
    rating?: number
    rate?: number
    is_graded?: boolean
}

interface StudentHomework {
    student_id: number
    student_name: string
    homeworks: HomeworkItem[]
}

const Homework = () => {
    const navigate = useNavigate()
    const { t } = useTranslation()
    const { loading, setLoading, setSelectedHomeworkStudent } = useProfileStore()
    const [activeFilter, setActiveFilter] = useState<HomeworkStatus>('active')
    const [studentsHomework, setStudentsHomework] = useState<StudentHomework[]>([])
    const [allHomeworkData, setAllHomeworkData] = useState<StudentHomework[]>([])
    const [badgeCountsFromAPI, setBadgeCountsFromAPI] = useState({ active: 0, rate: 0, close: 0 })

    // Fetch all homework data for badge indicators
    const fetchAllHomeworkData = async () => {
        try {
            const response = await api.get('/homework/parent/children/')
            if (response.data && response.data.data && response.data.data.data) {
                setAllHomeworkData(response.data.data.data)
                // Set badge counts from API response
                setBadgeCountsFromAPI({
                    active: response.data.data.active_is_graded_seen_count || 0,
                    rate: response.data.data.rate_is_graded_seen_count || 0,
                    close: 0 // No close count provided in API
                })
            }
        } catch (error) {
            console.error('Error fetching all homework data:', error)
        }
    }

    // Fetch homework data from API
    const fetchHomeworkData = async (status: HomeworkStatus) => {
        try {
            setLoading(true)
            const response = await api.get(`/homework/parent/children/?status=${status}`)
            if (response.data && response.data.data.data) {
                setStudentsHomework(response.data.data.data)
            }
        } catch (error) {
            console.error('Error fetching homework data:', error)
            setStudentsHomework([])
        } finally {
            setLoading(false)
        }
    }

    // Fetch data on component mount and when filter changes
    useEffect(() => {
        fetchHomeworkData(activeFilter)
    }, [activeFilter])

    // Fetch all homework data on component mount for badge indicators
    useEffect(() => {
        fetchAllHomeworkData()
    }, [])

    // Handle filter change
    const handleFilterChange = (status: HomeworkStatus) => {
        setActiveFilter(status)
    }

    const getFilterCounts = () => {
        const counts = { active: 0, rate: 0, close: 0 }
        studentsHomework.forEach(student => {
            student.homeworks.forEach(hw => {
                // Map API status to our filter status
                const mappedStatus = hw.status.toLowerCase() === 'active' ? 'active' :
                    hw.status.toLowerCase() === 'rated' ? 'rate' :
                    hw.status.toLowerCase() === 'closed' ? 'close' : null
                if (mappedStatus) {
                    counts[mappedStatus]++
                }
            })
        })
        return counts
    }

    const filterCounts = getFilterCounts()

    // Filter students based on current filter - all homeworks are already filtered by API
    const filteredStudents = studentsHomework.filter(student => student.homeworks.length > 0)

    const getStatusColor = (apiStatus: string) => {
        switch (apiStatus.toLowerCase()) {
            case 'green':
                return 'border-l-theme'
            case 'red':
                return 'border-l-red-500'
            default:
                return 'border-l-gray-300'
        }
    }

    // Check if homework is graded based on allHomeworkData
    const isHomeworkGraded = (homeworkId: number, studentId: number) => {
        const student = allHomeworkData.find(s => s.student_id === studentId)
        if (student) {
            const homework = student.homeworks.find(hw => hw.id === homeworkId)
            return homework?.is_graded || false
        }
        return false
    }

    return (
        <div className="pb-28">
            {/* Header */}
            <div className='mb-4 grid grid-cols-4 px-4 pt-2'>
                <div><IoIosArrowBack size={28} color='#000' onClick={()=>navigate("/inbox")}/></div>
                <div className='text-center col-span-2 text-lg font-medium'>Homework</div>
                <div></div>
            </div>

            {/* Filter Buttons */}
            <div className="px-4 mb-6">
                <div className="flex gap-3">
                    <button
                        onClick={() => handleFilterChange('active')}
                        className={`relative px-4 py-2 rounded-full text-sm font-medium flex items-center ${
                            activeFilter === 'active'
                                ? 'bg-theme text-white'
                                : 'bg-transparent text-gray-500'
                        }`}
                    >
                        <GoBook className="mr-1"/> 
                        Active
                        {badgeCountsFromAPI.active > 0 && (
                            <div className="absolute -top-2 -right-2 min-w-5 h-5 bg-red-500 rounded-full flex items-center justify-center">
                                <span className="text-white text-xs font-bold">{badgeCountsFromAPI.active}</span>
                            </div>
                        )}
                    </button>
                    
                    <button
                        onClick={() => handleFilterChange('rate')}
                        className={`relative px-4 py-2 rounded-full text-sm font-medium flex items-center ${
                            activeFilter === 'rate'
                                ? 'bg-yellow-500 text-white'
                                : 'bg-transparent text-gray-500'
                        }`}
                    >
                        <FaRegStar className="mr-1"/>
                        Rated
                        {badgeCountsFromAPI.rate > 0 && (
                            <div className="absolute -top-2 -right-2 min-w-5 h-5 bg-red-500 rounded-full flex items-center justify-center">
                                <span className="text-white text-xs font-bold">{badgeCountsFromAPI.rate}</span>
                            </div>
                        )}
                    </button>
                    
                    <button
                        onClick={() => handleFilterChange('close')}
                        className={`relative px-4 py-2 rounded-full text-sm font-medium flex items-center ${
                            activeFilter === 'close'
                                ? 'bg-red-500 text-white'
                                : 'bg-transparent text-gray-500'
                        }`}
                    >
                       <LiaCalendarTimes className="mr-1"/>
                        Closed
                        {badgeCountsFromAPI.close > 0 && (
                            <div className="absolute -top-2 -right-2 min-w-5 h-5 bg-red-500 rounded-full flex items-center justify-center">
                                <span className="text-white text-xs font-bold">{badgeCountsFromAPI.close}</span>
                            </div>
                        )}
                    </button>
                </div>
            </div>

            {/* Homework List */}
            <div className="px-4 space-y-6">
                {filteredStudents.map((student) => (
                    <div key={student.student_id}>
                        {/* Student Name */}
                        <h2 className="text-lg font-semibold text-gray-900 mb-3">
                            {student.student_name}
                        </h2>
                        
                        {/* Homework Items */}
                        <div className="space-y-3">
                            {student.homeworks.map((homework, ind) => (
                                <div className="relative" key={ind}>
                                    {/* Graded indicator badge */}
                                    {isHomeworkGraded(homework.id, student.student_id) && (
                                        <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full z-10"></div>
                                    )}
                                    
                                    <div
                                        key={homework.id}
                                        className={`bg-white rounded-lg border-l-4 ${getStatusColor(homework.status)} p-4 shadow-sm relative cursor-pointer hover:shadow-md transition-shadow`}
                                        onClick={() => {
                                            // Store the student info for the homework submission page
                                            setSelectedHomeworkStudent({
                                                id: student.student_id,
                                                name: student.student_name
                                            })
                                            navigate(`/homework/submit/${homework.id}`)
                                        }}
                                    >
                                        {/* Status indicator */}
                                        {homework.status === 'OVERDUE' && (
                                            <div className="absolute top-2 right-2 w-3 h-3 bg-red-500 rounded-full"></div>
                                        )}
                                    
                                    <div className="flex justify-between items-start">
                                        <div className="flex-1">
                                            <div className="flex justify-between items-start mb-1">
                                                <h3 className="font-semibold text-gray-900">
                                                    {homework.title}
                                                </h3>
                                                {activeFilter === 'rate' ? (
                                                    // Show rating stars for rate tab
                                                    homework.rate && (
                                                        <div className="flex gap-1 ml-2">
                                                            {[1, 2, 3, 4, 5].map(star => (
                                                                <span
                                                                    key={star}
                                                                    className={`text-sm ${
                                                                        star <= (homework.rate || 0) ? 'text-theme' : 'text-gray-300'
                                                                    }`}
                                                                >
                                                                    ★
                                                                </span>
                                                            ))}
                                                        </div>
                                                    )
                                                ) : (
                                                    // Show due date for other tabs
                                                    <div className="text-xs text-gray-500 ml-2 text-right">
                                                        <div>Due date:</div>
                                                    </div>
                                                )}
                                            </div>

                                            <div className="flex justify-between items-start mb-1">
                                                <p className="text-xs text-gray-500">
                                                    {homework.course_name} • {homework.branch_name}
                                                </p>
                                                <div className="text-xs text-gray-500 ml-2 text-right">
                                                    <div className="font-medium">
                                                        {new Date(homework.end_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                                                    </div>
                                                </div>
                                            </div>
                                            
                                        </div>
                                    </div>
                                </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    )
}

export default Homework